import type {
  CrmContactAssessmentFinding,
  CrmContactAssessmentMetric,
  CrmContactAssessmentResult,
  CrmContactAssessmentTone,
} from "@ease/crm/contact-assessment-results";
import {
  getCrmActionHrefKind,
  normalizeCrmActionHref,
  shouldOpenCrmActionHrefInNewTab,
} from "@ease/crm/safe-action-hrefs";
import { ExternalLink } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatDate } from "@/lib/i18n";

interface CrmAssessmentResultsCardProps {
  results: CrmContactAssessmentResult[];
  title?: string;
  description?: string;
  className?: string;
}

export function CrmAssessmentResultsCard({
  results,
  title = "Assessment results",
  description = "Configured test, diagnostic, onboarding, qualification, or review outcomes for this contact.",
  className,
}: CrmAssessmentResultsCardProps) {
  if (results.length === 0) return null;

  return (
    <section className={cn("rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-4", className)}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-xs font-medium uppercase text-[var(--color-muted-foreground)]">{title}</h3>
          <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">{description}</p>
        </div>
        <span className="shrink-0 rounded-full border border-[var(--color-border)] px-2.5 py-1 text-xs font-medium text-[var(--color-muted-foreground)]">
          {results.length}
        </span>
      </div>
      <div className="mt-4 space-y-4">
        {results.map((result) => (
          <section key={result.key} className="border-t border-[var(--color-border)] pt-4 first:border-t-0 first:pt-0">
            <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
              <div className="min-w-0">
                <h4 className="text-sm font-semibold text-[var(--color-foreground)]">{result.label}</h4>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {result.assessmentType ? <AssessmentChip label={result.assessmentType} /> : null}
                  {result.level ? <AssessmentChip label={result.level} tone="info" /> : null}
                  {result.score ? <AssessmentChip label={result.score} tone={result.tone} /> : null}
                  {result.outcome ? <AssessmentChip label={result.outcome} tone={result.tone} /> : null}
                </div>
              </div>
              {result.completedAt ? (
                <span className="shrink-0 text-xs text-[var(--color-muted-foreground)]">
                  {formatDate(result.completedAt)}
                </span>
              ) : null}
            </div>
            {result.summary ? (
              <p className="mt-3 text-sm text-[var(--color-muted-foreground)]">{result.summary}</p>
            ) : null}
            {result.findings.length > 0 ? (
              <div className="mt-3 grid gap-2 md:grid-cols-2">
                {result.findings.map((finding) => (
                  <AssessmentFindingCell key={`${result.key}:${finding.key}`} finding={finding} />
                ))}
              </div>
            ) : null}
            {result.metrics.length > 0 ? (
              <div className="mt-3 grid gap-2 md:grid-cols-2">
                {result.metrics.map((metric) => (
                  <AssessmentMetricCell key={`${result.key}:${metric.key}`} metric={metric} />
                ))}
              </div>
            ) : null}
            {result.href ? <AssessmentActionLink result={result} /> : null}
          </section>
        ))}
      </div>
    </section>
  );
}

function AssessmentActionLink({ result }: { result: CrmContactAssessmentResult }) {
  if (!result.href) return null;
  const href = normalizeCrmActionHref(result.href);
  if (!href) return null;

  const hrefKind = getCrmActionHrefKind(href);
  if (!hrefKind) return null;

  const label = result.actionLabel ?? "Open assessment";
  const openInNewTab = shouldOpenCrmActionHrefInNewTab(href);

  return (
    <a
      href={href}
      target={openInNewTab ? "_blank" : undefined}
      rel={openInNewTab ? "noreferrer" : undefined}
      className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-[var(--color-border)] px-2.5 py-1 text-xs font-medium text-[var(--color-foreground)] transition-colors hover:bg-[var(--color-muted)]"
    >
      <span>{label}</span>
      <ExternalLink className="h-3.5 w-3.5" />
    </a>
  );
}

function AssessmentChip({
  label,
  tone = "neutral",
}: {
  label: string;
  tone?: CrmContactAssessmentTone;
}) {
  return (
    <span className={cn(
      "rounded-full border px-2 py-0.5 text-[10px] font-medium capitalize",
      getCrmAssessmentToneClasses(tone),
    )}>
      {label}
    </span>
  );
}

function AssessmentFindingCell({ finding }: { finding: CrmContactAssessmentFinding }) {
  return (
    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          {finding.category ? (
            <p className="text-[10px] font-medium uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">
              {finding.category.replaceAll("_", " ")}
            </p>
          ) : null}
          <p className="mt-0.5 break-words text-sm font-medium text-[var(--color-foreground)]">{finding.label}</p>
        </div>
        {finding.tone !== "neutral" ? (
          <span className={cn(
            "shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-medium capitalize",
            getCrmAssessmentToneClasses(finding.tone),
          )}>
            {finding.tone}
          </span>
        ) : null}
      </div>
      {finding.value ? (
        <p className="mt-1 break-words text-sm text-[var(--color-foreground)]">{finding.value}</p>
      ) : null}
      {finding.description ? (
        <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">{finding.description}</p>
      ) : null}
    </div>
  );
}

function AssessmentMetricCell({ metric }: { metric: CrmContactAssessmentMetric }) {
  const percent = readCrmAssessmentMetricPercent(metric.value);

  return (
    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2">
      <div className="flex items-start justify-between gap-2">
        <p className="text-[11px] font-semibold uppercase text-[var(--color-muted-foreground)]">{metric.label}</p>
        {metric.tone !== "neutral" ? (
          <span className={cn(
            "shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-medium capitalize",
            getCrmAssessmentToneClasses(metric.tone),
          )}>
            {metric.tone}
          </span>
        ) : null}
      </div>
      <p className="mt-1 break-words text-sm text-[var(--color-foreground)]">{metric.value}</p>
      {percent !== null ? (
        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-[var(--color-muted)]" role="progressbar" aria-label={`${metric.label} ${metric.value}`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={percent}>
          <div className={cn("h-full rounded-full", getCrmAssessmentMetricBarClasses(metric.tone))} style={{ width: `${percent}%` }} />
        </div>
      ) : null}
      {metric.description ? (
        <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">{metric.description}</p>
      ) : null}
    </div>
  );
}

export function readCrmAssessmentMetricPercent(value: string): number | null {
  const match = /^(\d{1,3}(?:\.\d+)?)%$/.exec(value.trim());
  const rawPercent = match?.[1];
  if (!rawPercent) return null;
  const percent = Number.parseFloat(rawPercent);
  if (!Number.isFinite(percent) || percent < 0 || percent > 100) return null;
  return percent;
}

export function getCrmAssessmentMetricBarClasses(tone: CrmContactAssessmentTone): string {
  if (tone === "success") return "bg-[var(--color-success)]";
  if (tone === "warning") return "bg-[var(--color-warning)]";
  if (tone === "danger") return "bg-[var(--color-destructive)]";
  return "bg-[var(--color-primary)]";
}

export function getCrmAssessmentToneClasses(tone: CrmContactAssessmentTone) {
  if (tone === "success") {
    return "border-[var(--color-success)]/30 bg-[var(--color-success)]/10 text-[var(--color-success)]";
  }
  if (tone === "warning") {
    return "border-[var(--color-warning)]/30 bg-[var(--color-warning)]/10 text-[var(--color-warning)]";
  }
  if (tone === "danger") {
    return "border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]";
  }
  if (tone === "info") {
    return "border-[var(--color-info)]/30 bg-[var(--color-info)]/10 text-[var(--color-info)]";
  }
  return "border-[var(--color-border)] bg-[var(--color-card)] text-[var(--color-muted-foreground)]";
}
